import { useEffect, useRef, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  User, Mail, Lock, Building2, FileText, Tag,
  Upload, Eye, EyeOff, ChevronRight, ChevronLeft,
  CheckCircle2, Flame,
} from "lucide-react";
import TermsModal from "../components/TermsModal";

function toSlug(str) {
  return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");
}

const STEPS = ["Tu cuenta", "Tu hamburguesería", "Especialidad y logo"];

const DEFAULT_BURGER_TERMS = `TÉRMINOS Y CONDICIONES DE PARTICIPACIÓN — BURGER FEST

1. ACEPTACIÓN
Al registrarse en el Burger Fest, el participante acepta íntegramente los presentes Términos y Condiciones.

2. REQUISITOS DE PARTICIPACIÓN
- El participante debe ser mayor de edad o contar con autorización de su representante legal.
- El negocio registrado debe estar dedicado a la venta de hamburguesas o productos afines.
- Los productos presentados deben cumplir con las normas sanitarias y de calidad vigentes.
- Cada participante es responsable de la calidad, inocuidad y presentación de sus productos.

3. USO DE PLATAFORMA
- El participante se compromete a mantener actualizada la información de su negocio.
- Queda prohibido el uso de prácticas desleales, engañosas o que afecten la competencia justa.
- El organizador se reserva el derecho de suspender o eliminar cuentas que violen estas normas.

4. VOTACIÓN Y RESULTADOS
- Los votos se registran por dispositivo/usuario según la configuración del evento.
- Cualquier intento de manipulación de votos resultará en la descalificación inmediata.
- Los resultados publicados por el organizador son definitivos e inapelables.

5. PROPIEDAD INTELECTUAL
Los participantes conservan los derechos sobre sus recetas y marcas. Al participar autorizan el uso de su nombre comercial, logotipo y fotografías para la difusión del Burger Fest en todos los medios digitales y físicos.

6. RESPONSABILIDAD
El organizador no se hace responsable por daños, pérdidas o accidentes ocurridos durante el evento que sean atribuibles al participante o a terceros. El participante asume plena responsabilidad sobre su producto y la atención a sus clientes.

7. PRIVACIDAD Y DATOS
Los datos personales recopilados serán utilizados únicamente para la gestión del evento y la plataforma Qkiere. No serán compartidos con terceros sin consentimiento previo, salvo requerimiento legal.

8. MODIFICACIONES
El organizador se reserva el derecho de modificar estos términos con previo aviso a los participantes registrados. El uso continuado de la plataforma implica aceptación de los términos vigentes.

9. JURISDICCIÓN
Las partes se someten a las leyes aplicables del territorio donde se desarrolla el evento para la resolución de cualquier controversia derivada de estos términos.

Al completar el registro, declaro haber leído, comprendido y aceptado íntegramente los presentes Términos y Condiciones de participación en el Burger Fest.`;

// ── Genera polígono irregular para forma de carbón ───────────────────────────
function coalPoints(cx, cy, rx, ry, sides, seed) {
  let s = seed;
  const rand = () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
  return Array.from({ length: sides }, (_, j) => {
    const baseAngle  = (j / sides) * Math.PI * 2;
    const jitter     = (rand() - 0.5) * (Math.PI / sides) * 1.1;
    const angle      = baseAngle + jitter;
    const rf         = 0.55 + rand() * 0.55;
    return [(cx + Math.cos(angle) * rx * rf).toFixed(2), (cy + Math.sin(angle) * ry * rf).toFixed(2)].join(",");
  }).join(" ");
}

// ── Brasas encendidas ─────────────────────────────────────────────────────────
function CoalBed() {
  const coals = useMemo(() => {
    let seed = 137;
    const rand = () => { seed = (seed * 1664525 + 1013904223) & 0xffffffff; return (seed >>> 0) / 0xffffffff; };
    return Array.from({ length: 48 }, (_, i) => {
      const heat  = 0.28 + rand() * 0.72;
      const rx    = 5 + rand() * 12;
      const ry    = rx * (0.28 + rand() * 0.22);
      const sides = 5 + Math.floor(rand() * 4);
      const cx    = rand() * 100;
      const cy    = rand() * 100;
      const pts   = coalPoints(cx, cy, rx, ry, sides, Math.floor(rand() * 99999));
      const ca    = rand() * Math.PI;
      const cl    = rx * (0.25 + rand() * 0.35);
      const crack = {
        x1: (cx - Math.cos(ca) * cl * 0.4).toFixed(2), y1: (cy - Math.sin(ca) * cl * 0.25).toFixed(2),
        x2: (cx + Math.cos(ca) * cl * 0.6).toFixed(2), y2: (cy + Math.sin(ca) * cl * 0.3).toFixed(2),
      };
      return { id: i, pts, cx, cy, rx, ry, heat, crack, delay: rand() * 3, dur: 1.6 + rand() * 2.2, animated: heat > 0.45 };
    });
  }, []);

  return (
    <div className="fixed bottom-0 left-0 right-0 pointer-events-none overflow-hidden"
      style={{ height: "44vh", zIndex: 1 }}>
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="rbedGlow" cx="50%" cy="80%" r="60%">
            <stop offset="0%"   stopColor="#ff4400" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#ff2200" stopOpacity="0" />
          </radialGradient>
          {coals.map(c => {
            const hot  = c.heat > 0.72;
            const warm = c.heat > 0.44;
            return (
              <radialGradient key={c.id} id={`rcg${c.id}`} cx="42%" cy="28%" r="62%" gradientUnits="objectBoundingBox">
                <stop offset="0%"   stopColor={hot ? "#fff0a0" : warm ? "#ff7000" : "#cc3a00"} stopOpacity={hot ? 1 : 0.92} />
                <stop offset="28%"  stopColor={hot ? "#ff6000" : warm ? "#cc2800" : "#7a1500"} stopOpacity={0.88} />
                <stop offset="65%"  stopColor={hot ? "#8b1a00" : warm ? "#4a0c00" : "#1e0500"} stopOpacity={0.82} />
                <stop offset="100%" stopColor="#0a0100" stopOpacity={0.96} />
              </radialGradient>
            );
          })}
          <filter id="rcoalBlur" x="-30%" y="-30%" width="160%" height="160%"><feGaussianBlur stdDeviation="1.2" /></filter>
          <filter id="rcrackGlow" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="0.4" /></filter>
        </defs>

        <rect x="0" y="0" width="100" height="100" fill="url(#rbedGlow)" />

        {coals.filter(c => c.heat > 0.5).map(c => (
          <ellipse key={`rh-${c.id}`} cx={c.cx} cy={c.cy} rx={c.rx * 1.6} ry={c.ry * 1.8}
            fill={c.heat > 0.72 ? "#ff4400" : "#cc2200"} opacity={c.heat * 0.18} filter="url(#rcoalBlur)" />
        ))}

        {coals.map(c => c.animated ? (
          <motion.polygon key={c.id} points={c.pts} fill={`url(#rcg${c.id})`}
            animate={{ opacity: [c.heat * 0.78, c.heat * 0.98, c.heat * 0.62, c.heat * 0.92, c.heat * 0.78] }}
            transition={{ duration: c.dur, repeat: Infinity, delay: c.delay, ease: "easeInOut" }} />
        ) : (
          <polygon key={c.id} points={c.pts} fill={`url(#rcg${c.id})`} opacity={c.heat * 0.72} />
        ))}

        {coals.filter(c => c.heat > 0.60).map(c => (
          <motion.line key={`rc-${c.id}`}
            x1={c.crack.x1} y1={c.crack.y1} x2={c.crack.x2} y2={c.crack.y2}
            stroke={c.heat > 0.80 ? "#ffe090" : "#ff7700"}
            strokeWidth={0.28 + c.heat * 0.22} strokeLinecap="round"
            opacity={c.heat * 0.9} filter="url(#rcrackGlow)"
            animate={{ opacity: [c.heat * 0.6, c.heat, c.heat * 0.5, c.heat * 0.95, c.heat * 0.6] }}
            transition={{ duration: c.dur * 0.85, repeat: Infinity, delay: c.delay + 0.3, ease: "easeInOut" }} />
        ))}
      </svg>
    </div>
  );
}

// ── Chispas / partículas de brasa ─────────────────────────────────────────────
function EmberParticles() {
  const particles = useMemo(() => {
    let seed = 91;
    const rand = () => { seed = (seed * 1664525 + 1013904223) & 0xffffffff; return (seed >>> 0) / 0xffffffff; };
    return Array.from({ length: 28 }, (_, i) => ({
      id: i,
      x:      3 + rand() * 94,
      size:   1 + rand() * 2.8,
      color:  rand() > 0.6 ? "#ffe070" : rand() > 0.3 ? "#ff7700" : "#ff2200",
      dur:    2.0 + rand() * 3.2,
      delay:  rand() * 4,
      driftX: (rand() - 0.5) * 28,
      driftY: 110 + rand() * 100,
      wobble: (rand() - 0.5) * 8,
    }));
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 2 }}>
      {particles.map(p => (
        <motion.div key={p.id}
          style={{
            position: "absolute", left: `${p.x}%`, bottom: "5%",
            width: p.size, height: p.size * 1.4,
            borderRadius: "50% 50% 40% 40%",
            background: `radial-gradient(ellipse at 40% 35%, #fff8e0 0%, ${p.color} 55%, transparent 100%)`,
            boxShadow: `0 0 ${p.size * 3}px ${p.size}px ${p.color}88`,
          }}
          animate={{
            y: [0, -p.driftY * 0.5, -p.driftY],
            x: [0, p.wobble, p.driftX],
            opacity: [0, 0.95, 0.75, 0.4, 0],
            scale: [1, 0.85, 0.5, 0.2],
          }}
          transition={{ duration: p.dur, repeat: Infinity, delay: p.delay, ease: [0.25, 0.46, 0.45, 0.94] }}
        />
      ))}
    </div>
  );
}

// ── Campo de formulario ───────────────────────────────────────────────────────
function BurgerField({ icon, label, children, extra }) {
  return (
    <div>
      <label className="text-xs font-bold mb-1.5 block text-orange-300 uppercase tracking-wider">
        {label}
      </label>
      <div className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3 py-3 focus-within:border-orange-500 focus-within:bg-white/10 transition">
        <span className="text-orange-400 flex-shrink-0">{icon}</span>
        {children}
        {extra && <span className="flex-shrink-0">{extra}</span>}
      </div>
    </div>
  );
}

// ── Indicador de pasos ────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((label, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
            i === step
              ? "bg-gradient-to-r from-orange-500 to-red-600 text-white border-orange-500 shadow-lg shadow-orange-500/30"
              : i < step
              ? "bg-green-500/20 text-green-400 border-green-500/40"
              : "bg-white/5 text-white/30 border-white/10"
          }`}>
            {i < step ? <CheckCircle2 size={11} /> : <span>{i + 1}</span>}
            <span className="hidden sm:inline">{label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`w-6 h-0.5 rounded transition-all ${i < step ? "bg-orange-500" : "bg-white/10"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function RegisterBurgerFest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("event");

  const [step, setStep]               = useState(0);
  const [loading, setLoading]         = useState(false);
  const [showPass, setShowPass]       = useState(false);
  const [account, setAccount]         = useState({ name: "", email: "", password: "" });
  const [business, setBusiness]       = useState({ businessName: "", slug: "", description: "" });
  const [logo, setLogo]               = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [categoryId, setCategoryId]   = useState("");
  const [categories, setCategories]   = useState([]);
  const fileRef = useRef(null);

  const [eventTerms, setEventTerms]         = useState("");
  const [eventTitle, setEventTitle]         = useState("Burger Fest");
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    const base = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/$/, "");
    axios.get(`${base}/categories`)
      .then(({ data }) => {
        const raw = data?.data?.categories ?? data?.categories ?? data;
        setCategories(Array.isArray(raw) ? raw : []);
      })
      .catch(() => toast.error("Error cargando categorías"));
  }, []);

  useEffect(() => {
    if (!eventId) return;
    const base = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/$/, "");
    axios.get(`${base}/events/${eventId}`)
      .then(({ data }) => {
        const ev = data?.data?.event ?? data?.data ?? data;
        if (ev?.termsAndConditions) setEventTerms(ev.termsAndConditions);
        if (ev?.title) setEventTitle(ev.title);
      })
      .catch(() => {});
  }, [eventId]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.warn("El logo no debe superar 5 MB"); return; }
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const canAdvance = () => {
    if (step === 0) {
      if (!account.name.trim() || account.name.trim().length < 2)       { toast.warn("Ingresa tu nombre completo"); return false; }
      if (!account.email.trim() || !/\S+@\S+\.\S+/.test(account.email)) { toast.warn("Correo inválido"); return false; }
      if (!account.password || account.password.length < 6)             { toast.warn("La contraseña debe tener al menos 6 caracteres"); return false; }
      return true;
    }
    if (step === 1) {
      if (!business.businessName.trim()) { toast.warn("Ingresa el nombre del comercio"); return false; }
      if (!business.slug.trim())         { toast.warn("El slug no puede estar vacío"); return false; }
      return true;
    }
    return true;
  };

  const nextStep = () => { if (canAdvance()) setStep(s => s + 1); };
  const prevStep = () => setStep(s => s - 1);

  const doRegister = async (acceptedAt = null) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("userName",     account.name.trim());
      fd.append("email",        account.email.trim());
      fd.append("password",     account.password);
      fd.append("businessName", business.businessName.trim());
      fd.append("slug",         business.slug.trim());
      fd.append("description",  business.description.trim());
      fd.append("category",     categoryId);
      if (logo instanceof File) fd.append("logo", logo);
      if (acceptedAt) fd.append("termsAcceptedAt", acceptedAt);
      if (eventId)    fd.append("eventId", eventId);

      const res  = await API.post("/companies/public-register", fd);
      const data = res.data?.data ?? res.data;

      localStorage.setItem("token",        data.tokens.accessToken);
      localStorage.setItem("accessToken",  data.tokens.accessToken);
      localStorage.setItem("refreshToken", data.tokens.refreshToken ?? "");
      localStorage.setItem("user",         JSON.stringify(data.user));

      toast.success("¡Tu hamburguesería está en el Burger Fest! 🍔🔥");
      navigate("/company");
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.error || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryId) { toast.warn("Selecciona una categoría"); return; }
    setShowTermsModal(true);
  };

  const handleTermsAccept = ({ acceptedAt }) => {
    setShowTermsModal(false);
    doRegister(acceptedAt);
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden"
      style={{ background: "#000000" }}
    >
      <TermsModal
        isOpen={showTermsModal}
        onAccept={handleTermsAccept}
        onDecline={() => setShowTermsModal(false)}
        terms={eventTerms || DEFAULT_BURGER_TERMS}
        eventTitle={eventTitle}
        accentColor="orange"
        submitLabel="¡Acepto y me uno al Burger Fest!"
      />

      {/* ── Capas de fondo ── */}
      <CoalBed />
      <EmberParticles />

      {/* Gradiente oscurecedor de la zona de brasas hacia arriba */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(to top, transparent 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.85) 65%, #000 100%)",
          zIndex: 3,
        }}
      />

      {/* ── Contenido ── */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55 }}
        className="w-full max-w-lg relative"
        style={{ zIndex: 10 }}
      >

        {/* ── Logo flotante ── */}
        <div className="flex flex-col items-center mb-6">
          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/sat-burgerfest.png`}
              alt="Sat Burger Fest 2026"
              className="w-52 h-auto select-none"
              draggable={false}
              animate={{
                filter: [
                  "drop-shadow(0 18px 35px rgba(255,90,0,0.55)) drop-shadow(0 0 18px rgba(255,160,0,0.30))",
                  "drop-shadow(0 24px 50px rgba(255,120,0,0.80)) drop-shadow(0 0 30px rgba(255,200,0,0.55))",
                  "drop-shadow(0 18px 35px rgba(255,90,0,0.55)) drop-shadow(0 0 18px rgba(255,160,0,0.30))",
                ],
              }}
              transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          <p className="text-white/40 mt-4 text-sm">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-orange-400 font-bold hover:text-orange-300 transition">
              Inicia sesión
            </Link>
          </p>
        </div>

        <StepIndicator step={step} />

        {/* ── Tarjeta del formulario ── */}
        <div
          className="rounded-3xl overflow-hidden border border-white/10"
          style={{
            background:    "rgba(8, 2, 0, 0.75)",
            backdropFilter:"blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            boxShadow:     "0 0 0 1px rgba(255,80,0,0.12), 0 30px 90px rgba(0,0,0,0.8), 0 0 80px rgba(180,40,0,0.08)",
          }}
        >
          <form onSubmit={step === 2 ? handleSubmit : (e) => e.preventDefault()}>
            <AnimatePresence mode="wait">

              {/* Paso 1 — Cuenta */}
              {step === 0 && (
                <motion.div key="s0"
                  initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.25 }}
                  className="p-7 space-y-4"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,100,0,0.15)", border: "1px solid rgba(255,100,0,0.2)" }}>
                      <User size={20} className="text-orange-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-black text-white">Tu cuenta</h2>
                      <p className="text-white/35 text-xs">Credenciales de acceso al festival</p>
                    </div>
                  </div>

                  <BurgerField icon={<User size={15} />} label="Nombre completo">
                    <input type="text" placeholder="Ej: Carlos Mendoza"
                      value={account.name}
                      onChange={e => setAccount({ ...account, name: e.target.value })}
                      className="w-full outline-none text-sm text-white placeholder-white/20 bg-transparent" />
                  </BurgerField>

                  <BurgerField icon={<Mail size={15} />} label="Correo electrónico">
                    <input type="email" placeholder="correo@miburguer.com"
                      value={account.email}
                      onChange={e => setAccount({ ...account, email: e.target.value })}
                      className="w-full outline-none text-sm text-white placeholder-white/20 bg-transparent" />
                  </BurgerField>

                  <BurgerField icon={<Lock size={15} />} label="Contraseña"
                    extra={
                      <button type="button" onClick={() => setShowPass(!showPass)} className="text-white/25 hover:text-orange-400 transition">
                        {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    }>
                    <input type={showPass ? "text" : "password"} placeholder="Mínimo 6 caracteres"
                      value={account.password}
                      onChange={e => setAccount({ ...account, password: e.target.value })}
                      className="w-full outline-none text-sm text-white placeholder-white/20 bg-transparent" />
                  </BurgerField>
                </motion.div>
              )}

              {/* Paso 2 — Negocio */}
              {step === 1 && (
                <motion.div key="s1"
                  initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.25 }}
                  className="p-7 space-y-4"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,100,0,0.15)", border: "1px solid rgba(255,100,0,0.2)" }}>
                      <Building2 size={20} className="text-orange-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-black text-white">Tu hamburguesería</h2>
                      <p className="text-white/35 text-xs">Cuéntanos sobre tu negocio</p>
                    </div>
                  </div>

                  <BurgerField icon={<Building2 size={15} />} label="Nombre del comercio">
                    <input type="text" placeholder="Ej: La Burger de Don Pepe"
                      value={business.businessName}
                      onChange={e => setBusiness({ ...business, businessName: e.target.value, slug: toSlug(e.target.value) })}
                      className="w-full outline-none text-sm text-white placeholder-white/20 bg-transparent" />
                  </BurgerField>

                  <div>
                    <label className="text-xs font-bold mb-1.5 block text-orange-300 uppercase tracking-wider">URL de tu comercio</label>
                    <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl px-3 py-3 focus-within:border-orange-500 transition">
                      <span className="text-white/25 text-xs whitespace-nowrap">qkiere.com/</span>
                      <input type="text" value={business.slug}
                        onChange={e => setBusiness({ ...business, slug: toSlug(e.target.value) })}
                        className="w-full outline-none text-sm text-orange-400 font-bold bg-transparent"
                        placeholder="mi-hamburguesa" />
                    </div>
                  </div>

                  <BurgerField icon={<FileText size={15} />} label="Descripción (opcional)">
                    <textarea rows={3} placeholder="¿Qué hace especial tu hamburguesa?"
                      value={business.description}
                      onChange={e => setBusiness({ ...business, description: e.target.value })}
                      className="w-full outline-none text-sm text-white placeholder-white/20 bg-transparent resize-none" />
                  </BurgerField>
                </motion.div>
              )}

              {/* Paso 3 — Categoría y logo */}
              {step === 2 && (
                <motion.div key="s2"
                  initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.25 }}
                  className="p-7 space-y-5"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,100,0,0.15)", border: "1px solid rgba(255,100,0,0.2)" }}>
                      <span className="text-xl">🏆</span>
                    </div>
                    <div>
                      <h2 className="text-lg font-black text-white">Especialidad y logo</h2>
                      <p className="text-white/35 text-xs">¿En qué categoría compites?</p>
                    </div>
                  </div>

                  {/* Categorías */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Tag size={13} className="text-orange-400" />
                      <span className="text-xs font-black text-orange-300 uppercase tracking-widest">Categoría</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1
                      [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-orange-500/30 [&::-webkit-scrollbar-track]:bg-transparent">
                      {categories.map(cat => (
                        <button key={cat.id} type="button" onClick={() => setCategoryId(cat.id)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all text-left ${
                            categoryId === cat.id
                              ? "border-orange-500 bg-orange-500/20 text-white shadow-lg shadow-orange-500/20"
                              : "border-white/10 text-white/50 hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-white/80"
                          }`}>
                          {cat.icon && <span className="text-base">{cat.icon}</span>}
                          <span className="truncate leading-tight">{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Logo del negocio */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Upload size={13} className="text-orange-400" />
                      <span className="text-xs font-black text-orange-300 uppercase tracking-widest">Logo de tu negocio</span>
                      <span className="text-xs text-white/25 ml-auto">(opcional)</span>
                    </div>
                    <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={handleLogoChange} />
                    {logoPreview ? (
                      <div className="flex items-center gap-4 p-3 bg-white/5 rounded-2xl border border-white/10">
                        <img src={logoPreview} alt="preview"
                          className="w-16 h-16 rounded-xl object-cover border-2 border-orange-500/50" />
                        <div>
                          <p className="text-sm font-bold text-white truncate max-w-xs">{logo?.name}</p>
                          <button type="button" onClick={() => fileRef.current?.click()}
                            className="mt-1.5 text-xs text-orange-400 font-bold hover:text-orange-300 transition">
                            Cambiar imagen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileRef.current?.click()}
                        className="w-full border-2 border-dashed border-white/10 rounded-2xl py-7 flex flex-col items-center gap-2 text-white/25 hover:border-orange-500/50 hover:text-orange-400 transition-all hover:bg-orange-500/5">
                        <Upload size={26} />
                        <span className="text-sm font-bold">Sube el logo de tu negocio</span>
                        <span className="text-xs">PNG, JPG o WEBP · máx. 5 MB</span>
                      </button>
                    )}
                  </div>

                  {/* Aviso T&C */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 p-3 rounded-xl border border-orange-500/30 bg-orange-500/10"
                  >
                    <FileText size={16} className="text-orange-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white">Términos y Condiciones requeridos</p>
                      <p className="text-xs text-white/35 mt-0.5">
                        Al registrarte deberás leer y aceptar los T&C del {eventTitle}
                      </p>
                    </div>
                    <button type="button" onClick={() => setShowTermsModal(true)}
                      className="text-xs text-orange-400 font-bold hover:text-orange-300 transition flex-shrink-0">
                      Leer T&C
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navegación */}
            <div className="px-7 pb-7 flex gap-3">
              {step > 0 && (
                <button type="button" onClick={prevStep}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-white/10 text-white/50 hover:bg-white/5 font-semibold text-sm transition">
                  <ChevronLeft size={16} /> Atrás
                </button>
              )}
              {step < 2 ? (
                <button type="button" onClick={nextStep}
                  className="flex-1 flex items-center justify-center gap-2 font-black py-3 rounded-xl transition-all text-white shadow-lg"
                  style={{ background: "linear-gradient(135deg, #ff4500, #cc1500)", boxShadow: "0 4px 24px rgba(255,69,0,0.45)" }}
                  onMouseOver={e => e.currentTarget.style.boxShadow = "0 4px 36px rgba(255,80,0,0.7)"}
                  onMouseOut={e  => e.currentTarget.style.boxShadow = "0 4px 24px rgba(255,69,0,0.45)"}
                >
                  Siguiente paso <ChevronRight size={16} />
                </button>
              ) : (
                <button type="submit" disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 font-black py-3 rounded-xl transition-all text-white disabled:opacity-50 shadow-lg"
                  style={{ background: "linear-gradient(135deg, #ff4500, #cc1500)", boxShadow: "0 4px 24px rgba(255,69,0,0.45)" }}
                >
                  {loading
                    ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full inline-block" /> Registrando…</>
                    : <><Flame size={18} /> ¡Unirme al Burger Fest!</>
                  }
                </button>
              )}
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-white/20 mt-5">
          Al registrarte aceptas los{" "}
          <span
            className="underline cursor-pointer text-white/30 hover:text-orange-400 transition"
            onClick={() => eventTerms ? setShowTermsModal(true) : undefined}
          >
            términos y condiciones
          </span>{" "}
          {eventTerms ? `de ${eventTitle}` : "de Qkiere.com"}
        </p>
      </motion.div>
    </div>
  );
}
